import ModalCandidato from "./ModalCandidato";
import FiltrosColapsaveis, { campoFiltro } from "./FiltrosColapsaveis";
import React, { useState, useCallback, useEffect } from "react";
import { buscarCandidatos, obterPerfilPublico, interesseNoCandidato, AREAS_ATUACAO, SENIORIDADES } from "../services/api";
import { rotulo, rotuloLista } from "../utils/rotulos";

// Busca de candidatos (consent-based) — usada tanto na aba de /vagas quanto na página /buscar-candidatos.
// Self-contained: só os filtros + resultados (sem Header/gate). Co-criada com a Juliana (Nextal).
// Filtros no MESMO padrão da grade de Vagas: Área e Senioridade em dropdown + Disponibilidade.

const VAZIO = { area: "", senioridade: "", cidade: "", disp: "", modalidade: "", matchMin: "" };
function corMatch(m) { return m >= 70 ? "bg-green-100 text-green-800" : m >= 40 ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-600"; }
// Disponibilidade: value = nome do enum (backend faz Enum.TryParse). NaoBuscando nunca aparece (consentimento).
const DISPONIB = [["Disponivel", "Disponível"], ["AbertoAPropostas", "Aberto a propostas"]];
const MODALIDADES = [["Remoto", "Remoto"], ["Hibrido", "Híbrido"], ["Presencial", "Presencial"]];

function dispCor(d) {
  return d === "Disponível" ? "bg-green-100 text-green-800"
    : d === "Aberto a propostas" ? "bg-blue-100 text-blue-800"
    : "bg-gray-100 text-gray-600";
}

export default function BuscaCandidatos() {
  const [filtros, setFiltros] = useState(VAZIO);
  const [tags, setTags] = useState([]);     // palavras-chave (chips) — cada uma vira um filtro AND no backend
  const [tagInput, setTagInput] = useState("");
  const [rows, setRows] = useState(null); // null = ainda não buscou; [] = buscou e nada veio
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const buscar = useCallback(async (f) => {
    setCarregando(true); setErro(null);
    try { setRows(await buscarCandidatos(f)); }
    catch (e) { setErro(e.message); setRows([]); }
    finally { setCarregando(false); }
  }, []);

  // Ao abrir, já lista todos os candidatos disponíveis (entrar sem busca não pode vir vazio).
  useEffect(() => { buscar(VAZIO); }, [buscar]);

  const comTags = (fil, tgs) => ({ ...fil, q: tgs.join(",") });
  const submit = (e) => { e.preventDefault(); buscar(comTags(filtros, tags)); };
  const limpar = () => { setFiltros(VAZIO); setTags([]); setTagInput(""); buscar(VAZIO); };
  // Selects filtram na hora; campos de texto (cidade, match) aplicam no Enter/blur. Sem botão "Buscar".
  const set = (k, v) => { const f = { ...filtros, [k]: v }; setFiltros(f); buscar(comTags(f, tags)); };
  const setText = (k, v) => setFiltros((f) => ({ ...f, [k]: v }));
  const aplicarAgora = () => buscar(comTags(filtros, tags));

  const addTag = (raw) => {
    const t = raw.replace(/,/g, "").trim();
    if (!t || tags.some((x) => x.toLowerCase() === t.toLowerCase())) { setTagInput(""); return; }
    const novas = [...tags, t]; setTags(novas); setTagInput(""); buscar(comTags(filtros, novas));
  };
  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
    else if (e.key === "Backspace" && !tagInput && tags.length) { const novas = tags.slice(0, -1); setTags(novas); buscar(comTags(filtros, novas)); }
  };
  const removeTag = (i) => { const novas = tags.filter((_, idx) => idx !== i); setTags(novas); buscar(comTags(filtros, novas)); };

  const inp = campoFiltro;   // mesma classe das outras barras — é o que faz as três telas parecerem a mesma tela

  return (
    <div>
      <p className="text-gray-600 text-sm">
        Encontre quem está aberto a oportunidades. Só aparecem candidatos que escolheram ficar visíveis para empresas e recrutadores.
      </p>

      {/* Mesma barra colapsável das outras telas: filtrar é a mesma tarefa em toda lista do site. */}
      <form onSubmit={submit} className="mt-4">
        <FiltrosColapsaveis
          onLimpar={limpar}
          ajuda="Cada palavra-chave filtra o currículo: headline, resumo, skills, cargos e empresas onde trabalhou. Quanto mais tags, mais específico."
          resumo={[
            filtros.area && filtros.area,
            filtros.senioridade && filtros.senioridade,
            filtros.disp && filtros.disp,
            filtros.modalidade && `aceita ${filtros.modalidade}`,
            filtros.cidade && filtros.cidade,
            filtros.matchMin && `match ≥ ${filtros.matchMin}%`,
            ...tags.map((t) => `"${t}"`),
          ]}>
          <select className={inp} aria-label="Área de atuação" value={filtros.area} onChange={(e) => set("area", e.target.value)}>
            <option value="">Área (todas)</option>{AREAS_ATUACAO.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className={inp} aria-label="Senioridade" value={filtros.senioridade} onChange={(e) => set("senioridade", e.target.value)}>
            <option value="">Senioridade (todas)</option>{SENIORIDADES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={inp} aria-label="Disponibilidade" value={filtros.disp} onChange={(e) => set("disp", e.target.value)}>
            <option value="">Disponibilidade (todas)</option>{DISPONIB.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className={inp} aria-label="Modalidade" value={filtros.modalidade} onChange={(e) => set("modalidade", e.target.value)}>
            <option value="">Modalidade (todas)</option>{MODALIDADES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className={inp} placeholder="Localização (cidade)" value={filtros.cidade}
            onChange={(e) => setText("cidade", e.target.value)} onKeyDown={(e) => e.key === "Enter" && aplicarAgora()} onBlur={aplicarAgora} />
          <input type="number" min="0" max="100" step="5" className={inp} aria-label="Match mínimo (%)"
            placeholder="Match mín. %" value={filtros.matchMin}
            onChange={(e) => setText("matchMin", e.target.value)} onKeyDown={(e) => e.key === "Enter" && aplicarAgora()} onBlur={aplicarAgora} />

        {/* Palavras-chave como TAGS: digita + Enter (ou vírgula) adiciona à lista; o candidato precisa bater em TODAS.
            Ocupa a linha inteira da grade — é campo de texto livre, não cabe na largura de um select. */}
        <div className="sm:col-span-2 lg:col-span-3">
          <div className={`flex flex-wrap items-center gap-1.5 ${campoFiltro} py-1.5`}>
            {tags.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5">
                {t}
                <button type="button" onClick={() => removeTag(i)} aria-label={`Remover ${t}`} className="hover:text-red-600 leading-none">×</button>
              </span>
            ))}
            <input className="flex-1 min-w-[140px] text-sm outline-none py-0.5"
              placeholder={tags.length ? "+ palavra-chave" : "Palavras-chave (skill, cargo, termo) — Enter para adicionar"}
              value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={onTagKey} onBlur={() => addTag(tagInput)} />
          </div>
        </div>
        </FiltrosColapsaveis>
      </form>

      {/* Resultados */}
      <div className="mt-5">
        {carregando && <p className="text-gray-400 py-6">Buscando candidatos...</p>}
        {!carregando && erro && <p className="text-red-600 py-6">{erro}</p>}
        {!carregando && !erro && rows === null && (
          <p className="text-gray-500 py-6">Use os filtros acima e clique em <strong>Buscar</strong>. Deixe tudo em branco para ver todos os candidatos disponíveis.</p>
        )}
        {!carregando && !erro && rows && rows.length === 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-sm">
            Nenhum candidato encontrado com esses filtros. Tente termos mais amplos ou limpe os filtros.
          </div>
        )}
        {!carregando && !erro && rows && rows.length > 0 && (
          <>
            <p className="text-sm text-gray-500 mb-3">{rows.length} candidato{rows.length > 1 ? "s" : ""} encontrado{rows.length > 1 ? "s" : ""}</p>
            <div className="space-y-3">
              {rows.map((c) => <CandidatoCard key={c.usuarioId} c={c} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Chip({ children }) {
  return <span className="inline-block rounded-full bg-gray-100 text-gray-700 text-xs px-2.5 py-0.5">{children}</span>;
}

function CandidatoCard({ c }) {
  const [aberto, setAberto] = useState(false);
  const [perfil, setPerfil] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [interesse, setInteresse] = useState(null); // null | "enviando" | "ok" | "erro"

  const enviarInteresse = async () => {
    setInteresse("enviando");
    try { await interesseNoCandidato(c.usuarioId); setInteresse("ok"); }
    catch { setInteresse("erro"); }
  };

  const expandir = async () => {
    const vai = !aberto;
    setAberto(vai);
    if (vai && !perfil) {
      setCarregando(true); setErro(null);
      try { setPerfil(await obterPerfilPublico(c.usuarioId)); }
      catch (e) { setErro(e.message); }
      finally { setCarregando(false); }
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {c.match != null && <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${corMatch(c.match)}`}>{c.match}% match</span>}
            <span className="font-bold text-gray-900">{c.nome}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${dispCor(c.disponibilidade)}`}>{c.disponibilidade}</span>
            {c.temCurriculo && <span className="text-xs text-gray-400" title="tem currículo anexado">📎 CV</span>}
          </div>
          {c.headline && <div className="text-sm text-gray-600 mt-0.5">{c.headline}</div>}
          <div className="text-xs text-gray-500 mt-1">
            {[c.areaAtuacao, c.senioridade, c.cidade].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {interesse === "ok"
            ? <span className="text-green-700 text-xs font-semibold">Interesse enviado ✓</span>
            : <button onClick={enviarInteresse} disabled={interesse === "enviando"}
                className="rounded-lg bg-primary hover:bg-brand disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5">
                {interesse === "enviando" ? "..." : "Tenho interesse"}
              </button>}
          <button onClick={() => setAberto(true)} className="text-primary text-sm font-semibold hover:underline">
            Ver currículo completo
          </button>
        </div>
      </div>
      {interesse === "erro" && <p className="text-red-600 text-xs mt-1">Não foi possível enviar agora. Tente de novo.</p>}

      {(c.skills?.length > 0 || c.empresas?.length > 0 || c.modalidades?.length > 0) && (
        <div className="mt-3 space-y-1.5">
          {c.skills?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-xs text-gray-400">Skills:</span>
              {c.skills.slice(0, 12).map((s, i) => <Chip key={i}>{s}</Chip>)}
            </div>
          )}
          {c.empresas?.length > 0 && (
            <div className="text-xs text-gray-500"><span className="text-gray-400">Passagens:</span> {c.empresas.join(", ")}</div>
          )}
          {c.modalidades?.length > 0 && (
            <div className="text-xs text-gray-500"><span className="text-gray-400">Aceita:</span> {c.modalidades.map(rotulo).join(", ")}</div>
          )}
        </div>
      )}
      {aberto && (
        <ModalCandidato usuarioId={c.usuarioId} nome={c.nome} headline={c.headline}
          onFechar={() => setAberto(false)}
          rodape={
            interesse === "ok"
              ? <span className="text-green-700 text-sm font-semibold">Interesse enviado ✓ — o candidato foi avisado pelo eaiRH.</span>
              : <button onClick={enviarInteresse} disabled={interesse === "enviando"}
                  className="rounded-xl bg-primary hover:bg-brand disabled:opacity-60 text-white text-sm font-semibold px-4 py-2">
                  {interesse === "enviando" ? "Enviando..." : "Tenho interesse neste candidato"}
                </button>
          } />
      )}
    </div>
  );
}

function PerfilCompleto({ p }) {
  return (
    <div className="space-y-4 text-sm">
      {p.resumo && (
        <div>
          <h4 className="font-semibold text-gray-700 mb-1">Resumo</h4>
          <p className="text-gray-600 whitespace-pre-line">{p.resumo}</p>
        </div>
      )}
      {p.linkedinUrl && (
        <a href={p.linkedinUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-block">Ver LinkedIn ↗</a>
      )}
      {p.experiencias?.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-700 mb-1">Experiência</h4>
          <ul className="space-y-2">
            {p.experiencias.map((e, i) => (
              <li key={i}>
                <div className="font-medium text-gray-800">{e.cargo} {e.empresa && <span className="text-gray-500 font-normal">· {e.empresa}</span>}</div>
                {e.periodo && <div className="text-xs text-gray-400">{e.periodo}</div>}
                {e.descricao && <div className="text-gray-600 mt-0.5 whitespace-pre-line">{e.descricao}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {p.formacoes?.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-700 mb-1">Formação</h4>
          <ul className="space-y-1">
            {p.formacoes.map((f, i) => (
              <li key={i} className="text-gray-600">{f.curso} {f.instituicao && <span className="text-gray-500">· {f.instituicao}</span>} {f.periodo && <span className="text-xs text-gray-400">({f.periodo})</span>}</li>
            ))}
          </ul>
        </div>
      )}
      {p.certificados?.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-700 mb-1">Certificados</h4>
          <ul className="space-y-1">
            {p.certificados.map((c, i) => (
              <li key={i} className="text-gray-600">{c.nome} {c.emissor && <span className="text-gray-500">· {c.emissor}</span>} {c.ano && <span className="text-xs text-gray-400">({c.ano})</span>}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
